<template>
  <div class="top page-section light-blue-section">
    <h1
      class="info-title red"
    >
      Your last 5 months' <b>summary</b>
    </h1>
    <p class="section-subtitle">
      Here is a cashflow summary based on your income and spending from the <br>last 5 months
      <br v-if="showAdditionalText">
      <br v-if="showAdditionalText">
      <span v-if="showAdditionalText">Let's review your numbers and explore savings strategies to improve your cashflow.</span>
    </p>
    <div class="covered-months" v-if="(validCashflowMonths < 5 && !isDataMissing)">
      <img
        src="../../../assets/lightbulb.svg"
        alt="info"
      >
      <p>
        The numbers are based on available data covering {{ validCashflowMonths }} of the past 5 months.
      </p>
    </div>
    <div
      class="breakdown-table"
      :class="{ 'disabled': isDataMissing }"
    >
      <div
        v-if="isDataMissing"
        class="connect-another-subtitle missing-data-subtitle"
      >
        Unlock your last 5 months' summary by <b @click="handleConnectAccountsClick">connecting your current account</b>
      </div>
      <div class="breakdown-content">
        <div class="breakdown-row">
          <div class="col source">
            Average monthly <b>income</b>
          </div>
          <div class="col invest">
            £{{ numberToHumanReadable(monthlyAvgIncomeSpending.income) }}
          </div>
        </div>

        <div class="breakdown-row">
          <div class="col source">
            Average monthly <b>spending</b>
          </div>
          <div class="col invest">
            £{{ numberToHumanReadable(monthlyAvgIncomeSpending.spending) }}
          </div>
        </div>

        <div class="breakdown-row breakdown-total-row">
          <div class="col source">
            Your monthly <b>net balance</b>
          </div>
          <div
            class="col invest"
            :class="{ 'green-text': (flowType !== 'noBorrowAndSave' && monthlyAvgDisposableIncome.total >= 0) }"
          >
            £{{ numberToHumanReadable(monthlyAvgDisposableIncome.total) }}
          </div>
        </div>
      </div>
      <button
        v-if="showButton"
        class="light-blue-button"
        @click="handleButtonClick"
      >
        {{ buttonText }}
      </button>
      <div
        v-if="!isDataMissing"
        class="connect-another-subtitle"
      >
        Numbers look off? Get an accurate analysis by <b @click="handleConnectAccountsClick">connecting another account</b>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { numberToHumanReadable } from '../../../utils/miscUtils';

const props = defineProps({
  monthlyAvgIncomeSpending: {
    type: Object,
    required: true,
  },
  monthlyAvgDisposableIncome: {
    type: Object,
    required: true,
  },
  flowType: {
    type: String,
    required: true,
  },
  validCashflowMonths: {
    type: Number,
    default: 5,
  },
  showAdditionalText: {
    type: Boolean,
    default: false,
  },
  showButton: {
    type: Boolean,
    default: false,
  },
  isDataMissing: {
    type: Boolean,
    required: true,
  },
});

const emit = defineEmits(['buttonClick', 'connectAccountsClick']);

const BUTTON_TEXT_LABELS = {
  borrowAndSave: 'Get personalised loan offers',
  saveNoAccount: 'Get personalised saving offers',
  default: 'Get financial support',
};

const buttonText = computed(() => BUTTON_TEXT_LABELS[props.flowType] || BUTTON_TEXT_LABELS.default);

const handleButtonClick = () => {
  emit('buttonClick');
};

const handleConnectAccountsClick = () => {
  emit('connectAccountsClick');
};
</script>

<style lang="scss" scoped>
@import '../../../scss/riseup-vars';

.top {
  background-color: #EEF2F8;
  padding-bottom: 45px;
  padding-top: 45px;
}

.info-title {
  font-size: 36px;
  font-family: 'SalaryFinance';
  padding-top: 60px;
  padding-right: 25px;
  margin-bottom: 20px;
  text-align: left;
  width: 80%;
  margin-left: auto;
  margin-right: auto;
  font-weight: 100 !important;
  line-height: 1;
}

.red {
  color: #F14241;
}

.info-title b {
  font-weight: 700;
}

.section-subtitle {
  font-size: 17px;
  font-family: 'SalaryFinance';
  margin-top: 25px;
  margin-bottom: 28px;
  text-align: left;
  width: 80%;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.5;
}

.connect-another-subtitle {
  font-size: 14px;
  font-family: 'SalaryFinance';
  margin-bottom: 10px;
  margin-top: 10px;
  background: #FFE7C3;
  padding: 20px;
  border-radius: 16px;
  width: 100%;
  margin-left: auto;
  margin-right: auto;
  font-weight: 100 !important;
}

.missing-data-subtitle {
  background-color: #FFDC73;
  text-align: center;
}

.connect-another-subtitle b {
  font-weight: 700;
  text-decoration: underline;
  cursor: pointer;
}

.light-blue-button {
  background: #2C82FF;
  box-shadow: 0px 4px 21px rgba(149.13, 152.94, 149.88, 0.56);
  border-radius: 12px;
  color: white;
  padding: 17px 0px;
  border: none;
  width: 100%;
  cursor: pointer;
  font-size: 18px;
  font-family: 'SalaryFinance';
  font-weight: 700;
  margin-top: 40px;
  margin-bottom: 20px;
  margin-left: auto;
  margin-right: auto;
  display: block;
  padding-top: 16px;
  padding-bottom: 18px;
}

.breakdown-table {
  width: 90%;
  background-color: white;
  box-shadow: 0px 16px 46.4px 0px #00000026;
  border-radius: 25px;
  padding: 25px;
  margin-top: 20px;
  z-index: 2;
  margin: 0 auto;
  position: relative;
  padding-top: 30px;
}

.breakdown-table.disabled {
  background-color: #F1F5FA !important;
  box-shadow: 0px 16px 46.4px 0px #00000010 !important;
}

.breakdown-content {
  width: 100%;
}

.breakdown-row {
  display: flex;
  padding: 15px 5px;
  border-bottom: 1px solid rgba(32, 84, 65, 0.2);
  font-family: 'SalaryFinance';

  &:last-child {
    border-bottom: none;
  }
}

.col {
  color: #0C3467;
  .disabled & {
    color: #B0B0B0 !important;
  }

  &.source {
    flex: 4;
    text-align: left;
  }

  &.invest {
    flex: 1;
    text-align: right;
    font-weight: 700;
  }
}

.breakdown-total-row {
  font-weight: 100;
}

.breakdown-total-row b {
  font-weight: 700;
}

.breakdown-total-row .col.invest {
  &.green-text {
    color: #13C636;
  }
}

.covered-months {
  display: flex;
  gap: 12px;
  background-color: #DBE2F0;
  border-radius: 12px;
  padding: 16px;
  margin: 25px auto;
  width: 90%;

  p {
    margin: 0;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.5;
  }
}
</style>
